/**
 * OnDuplicateCard — renders the MySQL `ON DUPLICATE KEY UPDATE …` clause as
 * an expandable card. With `editContext` the assignments mount through
 * `SetAssignmentsList` routed via `conflictTail="on-duplicate"` (inline value
 * pills + add/remove, §2.4 S6b); without it the same list's disabled scaffold
 * scaffolding renders unchanged (§ 1.4). The span math stays renderer-side
 * either way — the tail-discriminated ops re-anchor through
 * `structure.onDuplicate` inside the applicator.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import { Button } from '../../../../../components/ui/button';
import type { VqeEditContext } from '../../edit/editContext';
import { useVqeStore } from '../../store/vqeStore';
import { ClauseCard } from '../select/ClauseCard';
import { VqeTooltip } from '../common/VqeTooltip';
import { SetAssignmentsList } from '../update/SetAssignmentsList';
import { parseAssignmentsList } from './parseAssignmentsList';

export interface OnDuplicateCardProps {
  readonly bufferText: string;
  readonly start: number;
  readonly end: number;
  readonly options: ParseOptions;
  /**
   * Commit-stable expand/collapse key seed (the workspace tab id) — see
   * `OnConflictCardProps.expansionScope`. A content-derived statement id here
   * would collapse the card after every assignment commit made from its own
   * expanded body, unmounting the edit controls mid-flow.
   */
  readonly expansionScope: string;
  /** Present only for plain-INSERT mounts — enables assignment editing. */
  readonly editContext?: VqeEditContext;
}

export function OnDuplicateCard({
  bufferText,
  start,
  end,
  options,
  expansionScope,
  editContext,
}: OnDuplicateCardProps) {
  const expansionKey = `${expansionScope}:on-duplicate`;
  const expanded = useVqeStore((s) => s.expansion[expansionKey] ?? false);
  const setExpansion = useVqeStore((s) => s.setExpansion);

  const upper = bufferText.slice(start, end).toUpperCase();
  // ON DUPLICATE KEY UPDATE col = expr, …
  const updateIdx = upper.indexOf('UPDATE');
  const assignmentsStart =
    updateIdx >= 0 ? start + updateIdx + 'UPDATE'.length : start;
  const rows = parseAssignmentsList(bufferText, assignmentsStart, end, options);

  return (
    <ClauseCard
      title="On duplicate key"
      testId="vqe-insert-on-duplicate"
      accessory={
        <span
          className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400"
          data-testid="vqe-insert-on-duplicate-action"
        >
          UPDATE
        </span>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <VqeTooltip content={expanded ? 'Collapse' : 'Expand'}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setExpansion(expansionKey, !expanded)}
              aria-expanded={expanded}
              data-testid="vqe-insert-on-duplicate-toggle"
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          </VqeTooltip>
          <span className="flex-1 text-xs text-muted-foreground" data-testid="vqe-insert-on-duplicate-summary">
            {rows.length} {rows.length === 1 ? 'assignment' : 'assignments'}
          </span>
        </div>
        {expanded ? (
          <div className="rounded-md border border-border bg-background p-2" data-testid="vqe-insert-on-duplicate-body">
            {/* Without an edit context SetAssignmentsList renders the disabled scaffold. */}
            <SetAssignmentsList
              bufferText={bufferText}
              start={assignmentsStart}
              end={end}
              options={options}
              testIdPrefix="vqe-insert-on-duplicate"
              editContext={editContext}
              conflictTail="on-duplicate"
            />
          </div>
        ) : null}
      </div>
    </ClauseCard>
  );
}
